/**
 * Browser-agnostic E2EE key provisioning (Part B).
 *
 * Factors the seal+store chain out of Class.svelte's QR-scan handler so it can be
 * reused from the Profile page (a parent on an E2EE-UNSUPPORTED browser — iPhone
 * Safari/Brave — can still PROVISION the media key by scanning the classroom QR
 * or pasting the raw key). The chain is pure libsodium + HTTP and does NOT need
 * insertable streams, so it works anywhere the identity keypair exists (the
 * keypair is provisioned at login gated only on the server flag).
 *
 * Two entry points:
 *   - {@link provisionKeyFromQrText}: full QR payload (verifies expiry + optional
 *     device signature), used by Class and Profile's scanner.
 *   - {@link provisionKeyFromManualText}: a pasted full QR-JSON payload OR a bare
 *     base64url key_material (in-person trust → skips signature/expiry).
 *
 * The LiveKit-only tail (rememberMediaKey / client.applyMediaKey / overlay
 * state) is intentionally NOT here — Class runs those extra steps itself after
 * calling {@link provisionKeyFromQrText}.
 */
import { storeSharedKey, getDeviceSignPubkey } from "./api";
import {
  whenReady,
  parseQrPayload,
  buildSharedKeyFromQr,
  encryptToPublicKey,
  keyIdForMaterial,
} from "./e2ee";
import {
  getMyPublicKey,
  DEVICE_KEY_SCOPE,
  DEVICE_KEY_SCOPE_ID,
} from "./e2eeSession";
import type { E2eeScope } from "@diy-avatar/shared/e2ee";

/**
 * Outcome of a provisioning attempt. `ok` carries the stored key's coordinates
 * so the caller can run any follow-up (e.g. Class installs the key live). On
 * failure, `reason` is a stable code the UI maps to an i18n string.
 */
export type ProvisionResult =
  | {
      ok: true;
      scope: E2eeScope;
      scope_id: number;
      key_material: string;
      key_id: string;
    }
  | {
      ok: false;
      reason:
        | "noIdentity"
        | "invalid"
        | "expired"
        | "badSig"
        | "storeError";
    };

/**
 * Provision a key from a scanned/decoded QR string.
 *
 * 1. Guard: the student must have an identity (pubkey) to seal to.
 * 2. Parse the payload.
 * 3. Best-effort fetch the issuing device's sign pubkey to AUTHENTICATE the QR
 *    (falls back to expiry-only when unavailable).
 * 4. Re-seal the key material to the student's own pubkey + POST it.
 */
export async function provisionKeyFromQrText(
  text: string,
): Promise<ProvisionResult> {
  // libsodium MUST be initialized before any crypto call below; the seal step is
  // synchronous and throws if ready hasn't resolved. Awaiting here keeps a throw
  // from escaping as an unhandled rejection in the scanner handler.
  await whenReady();

  const myPublicKey = getMyPublicKey();
  if (!myPublicKey) return { ok: false, reason: "noIdentity" };

  const parsed = parseQrPayload(text);
  if (!parsed) return { ok: false, reason: "invalid" };

  let deviceSignPubB64: string | undefined;
  if (parsed.device_id) {
    try {
      const resp = await getDeviceSignPubkey(parsed.device_id);
      deviceSignPubB64 = resp.sign_pubkey ?? undefined;
    } catch {
      // Could not fetch the device key — verification proceeds expiry-only.
    }
  }

  let built: ReturnType<typeof buildSharedKeyFromQr>;
  try {
    built = buildSharedKeyFromQr(text, myPublicKey, { deviceSignPubB64 });
  } catch {
    // A crypto failure (e.g. a malformed pubkey) is treated as a bad payload
    // rather than allowed to throw out of the provisioning chain.
    return { ok: false, reason: "invalid" };
  }
  if (!built) {
    // Distinguish expiry/signature failure from a malformed payload.
    const now = Date.now();
    if (typeof parsed.exp === "number" && now >= parsed.exp) {
      return { ok: false, reason: "expired" };
    }
    if (deviceSignPubB64) return { ok: false, reason: "badSig" };
    return { ok: false, reason: "invalid" };
  }

  try {
    await storeSharedKey(built.request);
  } catch {
    return { ok: false, reason: "storeError" };
  }
  return {
    ok: true,
    scope: built.payload.scope,
    scope_id: built.payload.scope_id,
    key_material: built.payload.key_material,
    key_id: built.payload.key_id,
  };
}

/**
 * Provision a key from MANUALLY pasted text (decision 4). Accepts either:
 *   - a full QR-JSON payload → delegated to {@link provisionKeyFromQrText}; OR
 *   - a bare base64url key_material (~43 chars, no JSON) → sealed under a fixed
 *     device scope (scope `device`, scope_id 0) with a derived content-addressed
 *     key_id.
 *
 * SECURITY NOTE: the bare-key path SKIPS signature + expiry verification. It is
 * an IN-PERSON trust path (the parent copies the key off a trusted screen), so
 * authenticity is established out-of-band rather than via the device signature.
 * Only use it when the key was obtained from a trusted source.
 */
export async function provisionKeyFromManualText(
  raw: string,
): Promise<ProvisionResult> {
  await whenReady();

  const text = raw.trim();
  if (!text) return { ok: false, reason: "invalid" };

  // A full QR payload (JSON) → reuse the verified scan path.
  if (text.startsWith("{")) {
    return provisionKeyFromQrText(text);
  }

  const myPublicKey = getMyPublicKey();
  if (!myPublicKey) return { ok: false, reason: "noIdentity" };

  // Bare base64url key material: must be a single token of base64url chars.
  if (!/^[A-Za-z0-9_-]+$/.test(text)) {
    return { ok: false, reason: "invalid" };
  }

  const key_id = keyIdForMaterial(text);
  let key_ciphertext: string;
  try {
    key_ciphertext = encryptToPublicKey(text, myPublicKey);
  } catch {
    return { ok: false, reason: "invalid" };
  }

  try {
    await storeSharedKey({
      scope: DEVICE_KEY_SCOPE,
      scope_id: DEVICE_KEY_SCOPE_ID,
      key_ciphertext,
      key_id,
    });
  } catch {
    return { ok: false, reason: "storeError" };
  }
  return {
    ok: true,
    scope: DEVICE_KEY_SCOPE,
    scope_id: DEVICE_KEY_SCOPE_ID,
    key_material: text,
    key_id,
  };
}
